/**
 * AssetQRLabel — QR sticker generator (offline, qrcode library)
 * รองรับพิมพ์แบบแผ่นสติกเกอร์ผ่าน PDF จริง (ขนาดหน่วย mm ตรงกับกระดาษจริง —
 * แม่นยำกว่าพิมพ์ผ่าน browser window มาก) ค่าเริ่มต้น: A4 48 ดวง (4×12)
 */
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Modal, Button, Group, Box, Text, Stack, Select, NumberInput, Collapse, ActionIcon, Tooltip } from '@mantine/core'
import { IconPrinter, IconDownload, IconQrcode, IconAdjustments } from '@tabler/icons-react'
import { useDisclosure } from '@mantine/hooks'
import QRCode from 'qrcode'
import jsPDF from 'jspdf'
import { SARABUN_REGULAR_B64, SARABUN_BOLD_B64 } from '../assets/fonts/sarabunFont'

interface AssetLabel {
  assetCode: string
  name: string
  department?: string
  serialNumber?: string
  location?: string
}
interface Props {
  opened: boolean
  onClose: () => void
  assets: AssetLabel[]
}

// (SIZES + drawLabel — canvas-based "custom size, plain paper" preview — removed:
// no longer used now that the sheet-mode PDF covers both large and compact formats)


// ── แผ่นสติกเกอร์สำเร็จรูป (หน่วย mm ทั้งหมด) ──
// ทั้งสองฟอร์แมตนี้เป็น grid ที่ออกแบบเองสำหรับพิมพ์ลงกระดาษ/สติกเกอร์เปล่า (ไม่ได้
// อิงสเปกยี่ห้อไหน) — ถ้าจะใช้กับแผ่นสติกเกอร์สำเร็จรูปที่มีรอยตัดจริงจากยี่ห้อใดยี่ห้อหนึ่ง
// ต้องตรวจสอบ margin/gap จากสเปกยี่ห้อนั้นก่อนเสมอ แล้วปรับผ่านปุ่มเยื้อง (calibration)
interface SheetFormat {
  key: string
  labelKey: string        // สำหรับแสดงชื่อฟอร์แมตใน UI
  pageW: number; pageH: number
  cols: number; rows: number
  labelW: number; labelH: number
  marginLeft: number; marginTop: number
  colGap: number; rowGap: number
  qrSize: number           // ขนาด QR ในหน่วย mm — กำหนดตรงๆ ต่อฟอร์แมต ไม่คำนวณจาก labelH อีกต่อไป
}
const SHEET_FORMATS: SheetFormat[] = [
  {
    key: 'a4-48', labelKey: 'qrLabel.formatA448',
    pageW: 210, pageH: 297, cols: 4, rows: 12,
    labelW: 46, labelH: 20,
    marginLeft: 8.5, marginTop: 12, colGap: 3, rowGap: 3,
    qrSize: 16,
  },
  {
    key: 'a4-compact', labelKey: 'qrLabel.formatA4Compact',
    pageW: 210, pageH: 297, cols: 5, rows: 13,
    labelW: 36, labelH: 18,
    marginLeft: 11, marginTop: 19.5, colGap: 2, rowGap: 2,
    qrSize: 10,
  },
]

export default function AssetQRLabel({ opened, onClose, assets }: Props) {
  const { t } = useTranslation()

  // ── Sticker sheet mode (แผ่นสติกเกอร์สำเร็จรูป) ──
  const [sheetFormat, setSheetFormat] = useState<string>('a4-48')
  const [customCols, setCustomCols] = useState(4)
  const [customRows, setCustomRows] = useState(10)
  const [fontFamily, setFontFamily] = useState<'sarabun' | 'helvetica'>('sarabun')
  const [fontScale, setFontScale] = useState(1)   // ตัวคูณขนาดตัวอักษร — 1 = ค่ามาตรฐาน
  const [startPosition, setStartPosition] = useState(1)   // เริ่มพิมพ์จากช่องที่เท่าไหร่ (เผื่อแผ่นเดิมใช้ไปบางส่วนแล้ว)
  const [offsetX, setOffsetX] = useState(0)                // ปรับเยื้องแนวนอน (mm) ถ้าเครื่องพิมพ์จริงคลาดจากสเปก
  const [offsetY, setOffsetY] = useState(0)                // ปรับเยื้องแนวตั้ง (mm)
  const [calibrationOpen, { toggle: toggleCalibration }] = useDisclosure(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)

  // ── Custom: คำนวณขนาดป้าย/margin ให้พอดี A4 เป๊ะ จากแค่ cols x rows ที่ user กำหนด ──
  // ใช้ margin/gap คงที่ที่สมเหตุสมผล (ไม่ต้องให้ user ปรับเอง ซับซ้อนเกินไป)
  const CUSTOM_MARGIN = 8
  const CUSTOM_GAP = 2
  const getActiveFormat = (): SheetFormat => {
    if (sheetFormat !== 'custom') return SHEET_FORMATS.find(f => f.key === sheetFormat)!
    const cols = Math.max(1, customCols)
    const rows = Math.max(1, customRows)
    const labelW = (210 - 2 * CUSTOM_MARGIN - (cols - 1) * CUSTOM_GAP) / cols
    const labelH = (297 - 2 * CUSTOM_MARGIN - (rows - 1) * CUSTOM_GAP) / rows
    return {
      key: 'custom', labelKey: 'qrLabel.formatCustom',
      pageW: 210, pageH: 297, cols, rows,
      labelW, labelH,
      marginLeft: CUSTOM_MARGIN, marginTop: CUSTOM_MARGIN, colGap: CUSTOM_GAP, rowGap: CUSTOM_GAP,
      qrSize: Math.max(6, Math.min(labelW, labelH) * 0.65),
    }
  }

  const generateSheetPdf = async (): Promise<jsPDF> => {
    const fmt = getActiveFormat()
    const perPage = fmt.cols * fmt.rows
    const pdf = new jsPDF({ unit: 'mm', format: [fmt.pageW, fmt.pageH] })

    // ── ฝังฟอนต์ไทย (Sarabun) เข้า PDF ── ฟอนต์ default ของ jsPDF (Helvetica) ไม่มี
    // ตัวอักษรไทยเลย ชื่อทรัพย์สิน/แผนกที่เป็นภาษาไทยจะพิมพ์ไม่ออกถ้าไม่ทำแบบนี้
    const pdfFontName = fontFamily === 'sarabun' ? 'Sarabun' : 'helvetica'
    if (fontFamily === 'sarabun') {
      pdf.addFileToVFS('Sarabun-Regular.ttf', SARABUN_REGULAR_B64)
      pdf.addFont('Sarabun-Regular.ttf', 'Sarabun', 'normal')
      pdf.addFileToVFS('Sarabun-Bold.ttf', SARABUN_BOLD_B64)
      pdf.addFont('Sarabun-Bold.ttf', 'Sarabun', 'bold')
    }

    // ตำแหน่งเริ่มต้น: ช่องที่ (startPosition-1) ของหน้าแรกถูกข้ามไป (แผ่นเดิมใช้ไปแล้วบางส่วน)
    const totalSlots = (startPosition - 1) + assets.length
    const totalPages = Math.max(1, Math.ceil(totalSlots / perPage))

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) pdf.addPage([fmt.pageW, fmt.pageH])

      for (let slot = 0; slot < perPage; slot++) {
        const globalSlot = page * perPage + slot
        const assetIdx = globalSlot - (startPosition - 1)
        if (assetIdx < 0 || assetIdx >= assets.length) continue // ช่องว่าง (ยังไม่ถึงคิว หรือหมดแล้ว)

        const asset = assets[assetIdx]
        const col = slot % fmt.cols
        const row = Math.floor(slot / fmt.cols)

        const x = fmt.marginLeft + col * (fmt.labelW + fmt.colGap) + offsetX
        const y = fmt.marginTop + row * (fmt.labelH + fmt.rowGap) + offsetY

        // QR code — เข้ารหัสแค่ assetCode (หน้า Scan ค้นข้อมูลเต็มจาก DB ต่อเอง)
        const qrDataUrl = await QRCode.toDataURL(asset.assetCode, {
          width: 300, margin: 0,
          color: { dark: '#0f172a', light: '#ffffff' },
          errorCorrectionLevel: 'M',
        })

        const qrSize = fmt.qrSize
        const qrX = x + 2
        const qrY = y + (fmt.labelH - qrSize) / 2
        pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize)

        // ข้อความด้านขวาของ QR — ขนาดฟอนต์ปรับตามความสูงของป้าย + fontScale ที่ user ปรับเอง
        const compact = fmt.labelH < 25
        const base = compact ? { code: 6, sub: 4.5 } : { code: 8, sub: 6 }
        const fs = (pt: number) => pt * fontScale
        const txX = qrX + qrSize + 2
        const maxTxW = fmt.labelW - qrSize - 6
        const labelBottom = y + fmt.labelH - 1.5
        let ty = y + (compact ? 4 : 5.5) * Math.min(fontScale, 1.3)

        pdf.setFont(pdfFontName, 'bold')
        pdf.setFontSize(fs(base.code))
        pdf.setTextColor(15, 23, 42)
        pdf.text(asset.assetCode, txX, ty, { maxWidth: maxTxW })
        ty += fs(base.code) * 0.45 + 1

        pdf.setFont(pdfFontName, 'normal')
        pdf.setFontSize(fs(base.code * 0.82))
        pdf.setTextColor(30, 41, 59)
        const nameLines = pdf.splitTextToSize(asset.name, maxTxW).slice(0, compact ? 1 : 2)
        if (ty < labelBottom) {
          pdf.text(nameLines, txX, ty)
          ty += nameLines.length * (fs(base.code * 0.82) * 0.42 + 0.8)
        }

        pdf.setFont(pdfFontName, 'normal')
        pdf.setFontSize(fs(base.sub))
        pdf.setTextColor(107, 114, 128)
        const detailLines = [
          asset.department ? `Dept: ${asset.department}` : null,
          asset.serialNumber ? `S/N: ${asset.serialNumber}` : null,
          asset.location ?? null,
        ].filter(Boolean) as string[]
        for (const line of detailLines) {
          if (ty >= labelBottom) break
          pdf.text(line, txX, ty, { maxWidth: maxTxW })
          ty += fs(base.sub) * 0.42 + 0.8
        }
      }
    }
    return pdf
  }

  const handleGenerateSheetPdf = async (mode: 'download' | 'print') => {
    if (assets.length === 0) return
    setGeneratingPdf(true)
    try {
      const pdf = await generateSheetPdf()
      if (mode === 'download') {
        pdf.save(`qr-labels-${sheetFormat}.pdf`)
      } else {
        pdf.autoPrint()
        window.open(pdf.output('bloburl') as unknown as string, '_blank')
      }
    } finally {
      setGeneratingPdf(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} size="xl" radius="lg"
      title={
        <Group gap={8}>
          <IconQrcode size={18} />
          <Text fw={600} style={{ fontFamily: 'Inter,sans-serif', fontSize: 15 }}>
            {t('qrLabel.title', 'QR Labels')} — {assets.length} {t('qrLabel.assetCount', 'asset(s)')}
          </Text>
        </Group>
      }>
      <Stack gap="md">

        {/* ── แผ่นสติกเกอร์สำเร็จรูป (โหมดหลัก) ── */}
        <Box style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
          <Text size="sm" fw={600} mb={8}>{t('qrLabel.sheetModeTitle', 'Print on sticker sheet')}</Text>

          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label={t('qrLabel.sheetFormat', 'Sheet format')}
              size="xs"
              value={sheetFormat}
              onChange={(v) => setSheetFormat(v ?? 'a4-48')}
              data={[
                ...SHEET_FORMATS.map(f => ({
                  value: f.key,
                  label: t(f.labelKey, `${f.cols * f.rows} labels — A4 (${f.labelW}×${f.labelH}mm)`),
                })),
                { value: 'custom', label: t('qrLabel.formatCustom', 'Custom size...') },
              ]}
              style={{ width: 260 }}
            />
            {sheetFormat === 'custom' && (
              <>
                <NumberInput
                  label={t('qrLabel.customCols', 'Columns')}
                  size="xs" min={1} max={12}
                  value={customCols}
                  onChange={(v) => setCustomCols(Math.max(1, Number(v) || 1))}
                  style={{ width: 100 }}
                />
                <NumberInput
                  label={t('qrLabel.customRows', 'Rows')}
                  size="xs" min={1} max={30}
                  value={customRows}
                  onChange={(v) => setCustomRows(Math.max(1, Number(v) || 1))}
                  style={{ width: 100 }}
                />
              </>
            )}
            <Select
              label={t('qrLabel.fontFamily', 'Font')}
              size="xs"
              value={fontFamily}
              onChange={(v) => setFontFamily((v ?? 'sarabun') as any)}
              data={[
                { value: 'sarabun', label: t('qrLabel.fontSarabun', 'Sarabun (Thai + English)') },
                { value: 'helvetica', label: t('qrLabel.fontHelvetica', 'Helvetica (English only)') },
              ]}
              style={{ width: 220 }}
            />
            <NumberInput
              label={t('qrLabel.fontSize', 'Text size')}
              size="xs"
              value={fontScale}
              onChange={(v) => setFontScale(Math.min(2, Math.max(0.5, Number(v) || 1)))}
              min={0.5} max={2} step={0.1} decimalScale={1}
              style={{ width: 110 }}
            />
            <NumberInput
              label={t('qrLabel.startPosition', 'Start at label #')}
              size="xs"
              value={startPosition}
              onChange={(v) => setStartPosition(Math.max(1, Number(v) || 1))}
              min={1}
              max={getActiveFormat().cols * getActiveFormat().rows}
              style={{ width: 140 }}
            />
            <Tooltip label={t('qrLabel.calibrationHint', 'Nudge alignment (mm) if your printer/paper differs slightly from spec')}>
              <ActionIcon variant="light" color="gray" size="lg" onClick={toggleCalibration}>
                <IconAdjustments size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {sheetFormat === 'custom' && (
            <Text size="xs" c="dimmed" mt={6}>
              {t('qrLabel.customPreview', 'Label size')}: {getActiveFormat().labelW.toFixed(1)}×{getActiveFormat().labelH.toFixed(1)}mm
              {' '}— {customCols * customRows} {t('qrLabel.perSheet', 'per sheet')}
            </Text>
          )}

          <Collapse in={calibrationOpen}>
            <Group gap="sm" mt="sm">
              <NumberInput label={t('qrLabel.offsetX', 'Horizontal offset (mm)')} size="xs"
                value={offsetX} onChange={(v) => setOffsetX(Number(v) || 0)} step={0.5} style={{ width: 180 }} />
              <NumberInput label={t('qrLabel.offsetY', 'Vertical offset (mm)')} size="xs"
                value={offsetY} onChange={(v) => setOffsetY(Number(v) || 0)} step={0.5} style={{ width: 180 }} />
            </Group>
          </Collapse>

          <Text size="xs" c="dimmed" mt={8}>
            {t('qrLabel.printScaleHint', 'When printing: set printer scale to 100% / Actual Size — not "Fit to Page" — or the labels will not align with the sheet.')}
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button size="sm" variant="light" color="teal" leftSection={<IconDownload size={14} />}
              loading={generatingPdf} onClick={() => handleGenerateSheetPdf('download')}>
              {t('qrLabel.downloadPdf', 'Download PDF')}
            </Button>
            <Button size="sm" leftSection={<IconPrinter size={14} />}
              loading={generatingPdf}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}
              onClick={() => handleGenerateSheetPdf('print')}>
              {t('qrLabel.printSheet', 'Print Sheet')}
            </Button>
          </Group>
        </Box>

        <Group justify="flex-end" pt="xs" style={{ borderTop: '1px solid #f1f5f9' }}>
          <Button variant="default" size="sm" onClick={onClose}>{t('qrLabel.close')}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
